import {
  AccountingDateCalculationStrategyEnum,
  AutoPaymentCollectionTimingEnum,
  BillGroupFormData,
  BillGroupStatusEnum,
  FirstInvoiceCreationStrategyEnum,
  IBillGroupReq,
  IBillGroupResp,
  IBillingSchedulePeriodItemsSchema,
  IBillingSchedulePeriodRespSchema,
  INetTerm,
  InvoiceCreationEnum,
  NetTermsEnum,
  QuoteSettingsDefaultAddressSourceEnum,
} from '@monetize/types/app';
import {
  convertEmailsStringToList,
  MimeTypeEnum,
} from '@monetize/types/common';
import { getFormattedDate, nullifyEmptyStrings } from '@monetize/utils/core';
import cloneDeep from 'lodash/cloneDeep';
import isString from 'lodash/isString';
import omitBy from 'lodash/omitBy';
import { unparse } from 'papaparse';
import { apiGet } from '../../../api/axios';
import { EXPORT } from '../../../constants';
import { saveFile } from '../../../utils/download';
import { buildMergePatch } from '../../../utils/mergePatch';

/**
 * Converts BillGroupResp to a form-compatible object with only BillGroupFormSchema fields
 */
export const convertBillGroupRespToFormData = (
  bgResp?: IBillGroupResp,
  defaultNetTerm?: INetTerm | null,
) => {
  // Default values
  if (!bgResp) {
    return {
      customId: '',
      name: '',
      status: BillGroupStatusEnum.ACTIVE,
      billDay: new Date().getDate(),
      netTerms: defaultNetTerm?.value || NetTermsEnum.NET_0,
      contactId: '',
      shippingContactId: '',
      defaultPaymentGatewayId: null,
      paymentMethodId: null,
      dunningProcessId: null,
      collectInvoiceBalanceAutomatically: true,
      autoPaymentCollectionTiming:
        AutoPaymentCollectionTimingEnum.INVOICE_GENERATION,
      autoEmailInvoice: true,
      invoiceCreation: InvoiceCreationEnum.AUTOMATIC,
      firstInvoiceCreationStrategy:
        FirstInvoiceCreationStrategyEnum.FOLLOW_EXISTING_SETTINGS,
      invoiceDelay: 0,
      purchaseOrderNumber: null,
      registrationNumber: null,
      vatNumber: '',
      legalEntityId: '',
      currency: 'USD',
      customFields: {},
      addressData: {
        addressSource: QuoteSettingsDefaultAddressSourceEnum.CONTACT,
      },
      accountingDateCalculationStrategy:
        AccountingDateCalculationStrategyEnum.BILLING_ANNIVERSARY,
      billingAccountId: null,
      linkedAccountId: null,
      strCcEmails: null,
      strBccEmails: null,
      outputLanguage: null,
    };
  }

  return {
    customId: bgResp.customId,
    name: bgResp.name,
    status: bgResp.status,
    billDay: bgResp.billDay,
    netTerms: bgResp.netTerms,
    contactId: bgResp.billingContactId,
    shippingContactId: bgResp.shippingContactId,
    defaultPaymentGatewayId: bgResp.defaultPaymentGateway?.id,
    paymentMethodId: bgResp.paymentMethodId,
    dunningProcessId: bgResp.dunningProcessId,
    collectInvoiceBalanceAutomatically:
      bgResp.collectInvoiceBalanceAutomatically,
    autoPaymentCollectionTiming: bgResp.autoPaymentCollectionTiming,
    autoEmailInvoice: bgResp.autoEmailInvoice,
    invoiceCreation: bgResp.invoiceCreation,
    firstInvoiceCreationStrategy: bgResp.firstInvoiceCreationStrategy,
    invoiceDelay: bgResp.invoiceDelay,
    strCcEmails: bgResp.ccEmails ? bgResp.ccEmails?.join(',') : null,
    strBccEmails: bgResp.bccEmails ? bgResp.bccEmails?.join(',') : null,
    purchaseOrderNumber: bgResp.purchaseOrderNumber,
    registrationNumber: bgResp.registrationNumber,
    vatNumber: bgResp.vatNumber,
    legalEntityId: bgResp.legalEntityId,
    currency: bgResp.currency,
    customFields: bgResp.customFields,
    addressData: {
      addressSource: bgResp.addressSource,
      billingAddressId: bgResp.billingAddress?.id,
      shippingAddressId: bgResp.shippingAddress?.id,
    },
    accountingDateCalculationStrategy: bgResp.accountingDateCalculationStrategy,
    billingAccountId: bgResp.billingAccountId || bgResp.accountId,
    linkedAccountId: bgResp.linkedAccountId,
    outputLanguage: bgResp.outputLanguage || null,
  };
};

/**
 * Converts the form's email strings into the email arrays the API takes,
 * splitting on the same commas, spaces and new lines the form validates with.
 */
export const convertBillGroupFormDataToReq = ({
  strCcEmails,
  strBccEmails,
  ...rest
}: BillGroupFormData): IBillGroupReq => ({
  ...rest,
  ccEmails: convertEmailsStringToList(strCcEmails),
  bccEmails: convertEmailsStringToList(strBccEmails),
});

/**
 * The only fields `PATCH /api/accounts/{accountId}/billGroups/{billGroupId}`
 * accepts `null` for. Every other field returns a 400 on `null`, because PUT
 * read `null` there as "keep the stored value" or "apply the default".
 */
const BILL_GROUP_PATCH_NULLABLE_FIELDS = new Set<string>([
  'paymentMethodId',
  'shippingContactId',
  'customId',
  'dunningProcessId',
  'ccEmails',
  'bccEmails',
  'purchaseOrderNumber',
  'vatNumber',
  'registrationNumber',
  'customFields',
]);

/**
 * The request body as the API layer sends it: the axios interceptor trims
 * strings and sends blank ones as `null`. Cloned first, because
 * `nullifyEmptyStrings` mutates nested objects shared with the form state.
 */
const toSubmittedReq = (values: BillGroupFormData) =>
  nullifyEmptyStrings(cloneDeep(convertBillGroupFormDataToReq(values)));

/**
 * Diffs the submitted form values against the values the form was loaded with
 * so the bill group PATCH only carries what the user actually changed.
 *
 * - Both sides are diffed as the API layer sends them, so whitespace-only
 *   edits are not patched and a field blanked to `null` is caught below.
 * - `billingAccountId` is not patchable (PUT never wrote it on update).
 * - `customFields` is merged key by key server-side, so a removed field is sent
 *   as `null`.
 * - A cleared field that PATCH cannot null (e.g. a removed linked account) is
 *   dropped, keeping the stored value as PUT did.
 */
export const buildBillGroupPatchPayload = (
  original: BillGroupFormData,
  updated: BillGroupFormData,
): Partial<IBillGroupReq> => {
  const { billingAccountId: _originalBillingAccountId, ...originalValues } =
    toSubmittedReq(original);
  const { billingAccountId: _updatedBillingAccountId, ...updatedValues } =
    toSubmittedReq(updated);
  const patch = buildMergePatch(originalValues, updatedValues, [
    'customFields',
  ]);
  return omitBy(
    patch,
    (value, key) =>
      value === null && !BILL_GROUP_PATCH_NULLABLE_FIELDS.has(key),
  );
};

type InvoiceFlattenedItem = Record<string, string | number>;

export const flattenInvoices = ({
  invoices,
  billGroupId,
  accountId,
}: {
  invoices: Record<string, IBillingSchedulePeriodRespSchema[]>;
  billGroupId: string;
  accountId: string;
}) => {
  const flattenedData: InvoiceFlattenedItem[] = [];

  Object.entries(invoices).forEach(([contractId, invoiceList]) => {
    invoiceList.forEach((invoice) => {
      invoice.items.forEach((item: IBillingSchedulePeriodItemsSchema) => {
        const flattenedItem: InvoiceFlattenedItem = {
          contractId,
          billGroupId,
          accountId,
          invoiceDate: invoice.invoiceDate,
          invoiceId: invoice.invoiceId ?? '',
          status: invoice.status,
          invoiceStatus: invoice.invoiceStatus ?? '',
          servicePeriodStart: item.servicePeriod.start,
          servicePeriodEnd: item.servicePeriod.end,
          offeringName: item.offeringName,
          productName: item.productName,
          quantity: item.quantity,
          pricePerUnit: item.pricePerUnit,
          amount: item.amount,
        };
        flattenedData.push(flattenedItem);
      });
    });
  });
  return flattenedData;
};

export const downloadInvoices = async ({ contractIds, billGroup }: any) => {
  const { endpoint: _endpoint, fields } =
    EXPORT.EXPORT_CONFIGURATION.billingSchedule;
  const currentDate = getFormattedDate(new Date(), 'yyyy-MM-dd');
  const headers = [...fields];

  const allResults: Record<string, any> = {};
  if (contractIds.length > 1) {
    for (const contractId of contractIds) {
      const endpoint = isString(_endpoint) ? _endpoint : _endpoint(contractId);

      const results = await apiGet<any>(endpoint);
      allResults[contractId] = results.data.periods;
    }
  } else {
    const singleContractId = contractIds[0];
    const endpoint = isString(_endpoint)
      ? _endpoint
      : _endpoint(singleContractId);

    const results = await apiGet<any>(endpoint);
    allResults[singleContractId] = results.data.periods;
  }

  const csv = unparse(
    {
      fields: headers,
      data: flattenInvoices({
        invoices: allResults,
        billGroupId: billGroup.id,
        accountId: billGroup.accountId,
      }),
    },
    { header: true },
  );
  saveFile(
    csv,
    `${currentDate}-bill-schedule-${billGroup.accountId}.csv`,
    MimeTypeEnum.CSV,
  );
};
